/**
 * context-panes.js — what row 2 shows for each scope.
 *
 * One builder per scope kind. Each returns a fragment of controls; the
 * `ContextZone` owns mounting, the morph and the announcement.
 *
 * ── The rule these exist to enforce ───────────────────────────────────────
 * **A pane never branches on `editableType` or on shared-vs-normal itself.**
 * It asks `section-command-service.js` what a section can do and WHY NOT, and
 * renders that answer. The first plan draft assumed "Replace" was a universal
 * fallback — it is not; buttons, containers and embeds do not share it — and
 * that assumption is only impossible to repeat if the branching lives in one
 * registry rather than in each pane.
 *
 * ── Disabled with the reason, BEFORE the click ────────────────────────────
 * A peer-locked section, a read-only tab and a shared nav all disable their
 * verbs up front, with the reason in the ACCESSIBLE NAME — not in a tooltip.
 * Disabled buttons do not reliably receive hover or focus, so a tooltip on one
 * is a reason nobody can read. `_syncHistoryChrome` in CMD-2 does the same
 * thing for undo/redo and for the same reason.
 *
 * Failing after the click was the alternative, and it is worse in a specific
 * way: the user has already committed to an action the product had no
 * intention of performing.
 *
 * ── Destructive actions do not confirm ────────────────────────────────────
 * Contracts §9 and the standing picker-patterns rule: act, then
 * `showUndoToast`. The undo IS the confirmation. `section-command-service.js`
 * passes `skipConfirm: true` deliberately and in ONE place, because
 * `removeSection`/`bulkDeleteSections` confirm by DEFAULT — a pane that forgot
 * would get a dialog in row 2, which the design forbids.
 */

import { PROJECT_LABELS, HELP_LABELS } from '../../shared/ui-vocabulary.js';
import { eventBus } from '../../core/events.js';
import { reportError } from '../../core/report-error.js';
import { store, readOnlyReasonFor } from '../../core/state.js';
import { scheduleSummary } from '../../shared/schedule-state.js';
import { scheduleDrawerEnabled } from '../section-properties/schedule-drawer.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { openPopoverMenu } from '../popover-menu.js';
import * as sectionService from './section-command-service.js';
import * as textService from './text-command-service.js';
import * as imageService from './image-command-service.js';
import * as logoService from './logo-command-service.js';
import { serviceFor } from './element-services.js';
import { kindLabel } from '../../shared/element-kind.js';
import { targetRefKey } from '../../shared/target-ref.js';
import { capabilitiesFor, isKnownEditableType, CONTROL } from './editable-capabilities.js';
import { beginOperation, bindOperationControl, endOperation } from './command-operation-state.js';

/** Row-2 label: the scope's name, with its parent when there is one. */
function paneLabel(vm, accent) {
  const el = document.createElement('span');
  el.className = 'pb-cb-pane-label';

  const swatch = document.createElement('span');
  swatch.className = 'pb-cb-pane-swatch';
  swatch.dataset.accent = accent;
  swatch.setAttribute('aria-hidden', 'true');

  const name = document.createElement('span');
  name.className = 'pb-cb-pane-name';
  name.textContent = vm.label || '';
  el.append(swatch, name);

  if (vm.sublabel) {
    const sub = document.createElement('em');
    sub.className = 'pb-cb-pane-sub';
    sub.textContent = `· ${vm.sublabel}`;
    el.appendChild(sub);
  }
  return el;
}

function divider() {
  const el = document.createElement('span');
  el.className = 'pb-cb-divider';
  el.setAttribute('aria-hidden', 'true');
  return el;
}

/**
 * One control. `disabledReason` is the whole point of the signature: a null
 * reason enables, a string disables AND becomes the accessible name.
 */
function paneButton({
  label,
  glyph,
  reason,
  onClick,
  onAsyncClick,
  operationId,
  busyLabel,
  errorScope = 'Command bar action',
  danger = false,
  shortcut,
  iconOnly = false,
  tooltip = null
}) {
  const run =
    typeof onAsyncClick === 'function'
      ? async (event) => {
          const token = beginOperation(operationId, { label: busyLabel || `${label}…` });
          if (token === null) return;
          try {
            await onAsyncClick(event);
          } catch (err) {
            reportError(errorScope, err, { user: 'toast' });
          } finally {
            endOperation(token);
          }
        }
      : onClick;
  const btn = createButton({
    variant: danger ? 'bare' : 'bare',
    className: ['pb-cb-pane-btn', danger && 'pb-cb-pane-btn-danger', iconOnly && 'pb-cb-icon']
      .filter(Boolean)
      .join(' '),
    icon: glyph ? icon(glyph, { size: 'sm' }) : undefined,
    label: iconOnly ? undefined : label,
    labelClass: 'pb-btn-label',
    ariaLabel: reason ? `${label} — ${reason}` : label,
    tooltip: reason || tooltip || label,
    tooltipShortcut: shortcut,
    onClick: run
  });
  if (operationId) {
    bindOperationControl(btn, operationId, { busyLabel: busyLabel || `${label}…` });
  }
  if (reason) {
    btn.disabled = true;
    // Belt and braces with `disabled`: a disabled button is skipped by some
    // screen-reader navigation modes, so the state is also announced.
    btn.setAttribute('aria-disabled', 'true');
  }
  return btn;
}

// ── page ────────────────────────────────────────────────────────────────────

/**
 * Nothing selected. The pane offers what you do TO a page, in the order the
 * work usually happens: add content, change its look, describe it.
 */
export function buildPagePane(vm) {
  const frag = document.createDocumentFragment();
  const readOnly = vm.writable ? null : readOnlyReasonFor(store.getPrimaryBlocker());

  // No pane label at page scope. The other scopes need one because "Hero" or
  // "Button" is the only thing naming what row 2 is acting on; the resting page
  // is already named by row 1 and by the nav sidebar's selected page, so a third
  // copy spent the row's width restating what nothing else was competing with.
  frag.append(
    paneButton({
      label: 'Add section',
      glyph: 'plus',
      reason: readOnly,
      onClick: () => eventBus.emit('palette:open-add-section')
    }),
    paneButton({
      label: 'Swap template',
      glyph: 'templates',
      reason: readOnly,
      onClick: () => eventBus.emit('toolbar:open-templates')
    }),
    divider(),
    paneButton({
      label: PROJECT_LABELS.design,
      glyph: 'skin',
      onClick: () => eventBus.emit('props:request-tab', { tab: 'theme' })
    }),
    paneButton({
      label: 'Page SEO',
      glyph: 'globe',
      onClick: () => eventBus.emit('props:request-tab', { tab: 'client-seo' })
    }),
    paneButton({
      label: PROJECT_LABELS.links,
      glyph: 'links',
      onClick: () => eventBus.emit('toolbar:open-link-manager')
    }),
    divider(),
    paneButton({
      // ⌘K gets a LABELLED home. The old bar's hint chip and its menu-hunt
      // heuristic were a confession that the bar could not show its own
      // contents; a named button in the resting pane is the fix that makes the
      // hint deletable in CMD-9.
      label: HELP_LABELS.find,
      glyph: 'search',
      shortcut: 'Ctrl+K',
      onClick: () => eventBus.emit('toolbar:open-command-palette')
    })
  );
  return frag;
}

// ── section ─────────────────────────────────────────────────────────────────

const SECTION_BUTTONS = Object.freeze({
  replace: { label: 'Replace', glyph: 'templates' },
  background: { label: 'Background', glyph: 'skin' },
  spacing: { label: 'Spacing', glyph: 'sliders' },
  visibility: { label: 'Visible', glyph: 'eye' },
  'move-up': { label: 'Move up', glyph: 'chevronUp', iconOnly: true, shortcut: 'Alt+↑' },
  'move-down': { label: 'Move down', glyph: 'chevronDown', iconOnly: true, shortcut: 'Alt+↓' },
  duplicate: { label: 'Duplicate', glyph: 'duplicate', iconOnly: true, shortcut: 'Ctrl+D' },
  delete: { label: 'Delete', glyph: 'trash', iconOnly: true, danger: true, shortcut: 'Del' },
  reset: { label: 'Reset', glyph: 'reset', iconOnly: true },
  'all-settings': { label: 'All settings', glyph: 'sliders' }
});

/**
 * Which action opens which surface. Verbs go to the service; routes go here.
 *
 * BC-6: a setting button opens the drawer it NAMES. Background, Spacing and
 * Visible all used to emit `props:request-tab {tab:'properties'}` — the same
 * payload as All settings — so clicking Spacing opened the general inspector.
 * The trigger rides along so the drawer anchors to it and returns focus to it.
 */
const openDrawer = (drawer) => (id, triggerEl) =>
  eventBus.emit('props:open-section-drawer', { instanceId: id, drawer, triggerEl });
const SECTION_ROUTES = Object.freeze({
  replace: (id, triggerEl) => eventBus.emit('section:request-swap', { instanceId: id, triggerEl }),
  background: openDrawer('background'),
  spacing: openDrawer('spacing'),
  visibility: openDrawer('schedule'),
  'all-settings': () => eventBus.emit('props:request-tab', { tab: 'properties' })
});

function findPageSection(instanceId) {
  for (const page of store.getState()?.pages || []) {
    const hit = (page.sections || []).find((s) => s?.instanceId === instanceId);
    if (hit) return hit;
  }
  return null;
}

/**
 * BC-6. The "Visible" control is really the section's SCHEDULE — there is no
 * other section-level visibility — so it says which state the section is in
 * and opens the Schedule drawer. Where scheduling does not apply (the shared
 * navigation and footer, or the feature's kill switch) it is absent rather
 * than a button that silently goes somewhere else.
 * @returns {{label: string, tooltip: string}|null}
 */
function visibilitySpec(instanceId, caps) {
  if (caps.sharedType || !scheduleDrawerEnabled()) return null;
  const summary = scheduleSummary(findPageSection(instanceId)?.schedule);
  return summary
    ? { label: 'Scheduled', tooltip: `Scheduled: ${summary}` }
    : { label: 'Always visible', tooltip: 'Always visible — set a schedule' };
}

export function buildSectionPane(vm, service = sectionService) {
  const frag = document.createDocumentFragment();
  const id = vm.instanceId;
  // Ask; never infer. `sectionCapabilities` is where shared nav/footer,
  // peer locks, read-only tabs and first/last-in-order all resolve to a
  // per-action reason.
  const caps = vm.capabilities || service.sectionCapabilities(id) || { disabledReason: {} };

  frag.appendChild(paneLabel(vm, 'section'));

  let lastWasVerb = false;
  for (const action of service.SECTION_ACTIONS) {
    let spec = SECTION_BUTTONS[action];
    if (!spec) continue;
    if (action === 'visibility') {
      const visibility = visibilitySpec(id, caps);
      if (!visibility) continue;
      spec = { ...spec, ...visibility };
    }
    const isVerb = Boolean(spec.iconOnly);
    if (isVerb && !lastWasVerb) frag.appendChild(divider());
    if (!isVerb && lastWasVerb) frag.appendChild(divider());
    lastWasVerb = isVerb;

    const route = SECTION_ROUTES[action];
    frag.appendChild(
      paneButton({
        ...spec,
        reason: caps.disabledReason?.[action] || null,
        onClick: route ? (event) => route(id, event?.currentTarget || null) : undefined,
        onAsyncClick: route ? undefined : () => runSectionAction(action, id, service),
        operationId: route ? null : `section:${id}:${action}`,
        errorScope: `Section ${action}`
      })
    );
  }
  return frag;
}

function runSectionAction(action, instanceId, service) {
  if (action === 'move-up') return service.moveByDirection(instanceId, -1);
  if (action === 'move-down') return service.moveByDirection(instanceId, 1);
  if (action === 'duplicate') return service.duplicate(instanceId);
  // Destructive, and deliberately NOT confirmed: the service passes
  // `skipConfirm: true` and the undo toast is the confirmation.
  if (action === 'delete') return service.remove(instanceId);
  if (action === 'reset') return service.reset(instanceId);
  return Promise.resolve(false);
}

// ── multi ───────────────────────────────────────────────────────────────────

/**
 * More than one section selected. Every verb here is a BULK verb — the plural
 * is in the label so the blast radius is visible before the click, which is the
 * only warning a no-confirm design gets to give.
 */
export function buildMultiPane(vm, service = sectionService) {
  const frag = document.createDocumentFragment();
  const count = vm.count || 0;
  // Ask the service, exactly as the section pane does. A read-only tab, a
  // peer-locked member and a shared nav each produce their own reason, and none
  // of that branching happens here.
  const caps = service.multiCapabilities();
  const why = (action) => caps.disabledReason?.[action] || null;

  frag.append(
    paneLabel(vm, 'section'),
    paneButton({
      label: 'Move up',
      glyph: 'chevronUp',
      reason: why('move-up'),
      onAsyncClick: () => service.bulkMove('up'),
      operationId: 'multi:move-up',
      errorScope: 'Move selected sections'
    }),
    paneButton({
      label: 'Move down',
      glyph: 'chevronDown',
      reason: why('move-down'),
      onAsyncClick: () => service.bulkMove('down'),
      operationId: 'multi:move-down',
      errorScope: 'Move selected sections'
    }),
    paneButton({
      label: `Duplicate ${count}`,
      glyph: 'duplicate',
      reason: why('duplicate'),
      onAsyncClick: () => service.bulkDuplicate(),
      operationId: 'multi:duplicate',
      errorScope: 'Duplicate selected sections'
    }),
    paneButton({
      // The count is IN the label because a no-confirm design gets exactly one
      // chance to show the blast radius, and this is it.
      label: `Delete ${count}`,
      glyph: 'trash',
      danger: true,
      reason: why('delete'),
      onAsyncClick: () => service.bulkRemove(),
      operationId: 'multi:delete',
      errorScope: 'Delete selected sections'
    }),
    divider(),
    paneButton({
      label: 'Clear selection',
      glyph: 'close',
      shortcut: 'Esc',
      onClick: () => eventBus.emit('selection:request', { intent: 'clear', source: 'command-bar' })
    })
  );
  return frag;
}

// ── text, image and every other typed element (CMD-6) ───────────────────────

/**
 * ONE builder for all three element scopes.
 *
 * The plan's rule — "controls are chosen by `editable-capabilities.js`; there is
 * **no universal Replace fallback**" — is structural here rather than
 * remembered: this function renders `capabilitiesFor(type).controls` in registry
 * order and has no default branch. A type the registry does not know renders its
 * label and nothing else, because there is nothing the registry said it could
 * do.
 *
 * That is why `button`, `logo`, `svg`, `container`, `embed`, `media` and `field`
 * are in the registry with `controls: []` and `allSettings: 'glass'`. An ABSENT
 * type and a type with an empty control list are different states, and only the
 * second is a decision anyone made.
 */
export function buildElementPane(vm, services = {}) {
  const text = services.text || textService;
  const image = services.image || imageService;
  const logo = services.logo || logoService;
  const injectedReplaceService = { image, logo }[vm.editableType] || null;
  const frag = document.createDocumentFragment();

  frag.appendChild(paneLabel(vm, 'element'));

  const type = vm.editableType;
  // Re-derive unless the view model carries a capability object of the RIGHT
  // SHAPE. `derivePaneViewModel` puts a section-shaped `capabilities` on section
  // scopes and an element-shaped one on element scopes; a mismatch here used to
  // throw `caps.controls is not iterable` and take the whole bar down mid-morph.
  // A pane must degrade, never crash: the bar is the only way back out.
  const supplied = vm.capabilities;
  const caps =
    supplied && Array.isArray(supplied.controls) && typeof supplied.disabledReason === 'function'
      ? supplied
      : capabilitiesFor(type);
  const ctx = {
    writable: vm.writable !== false,
    locked: Boolean(vm.locked),
    // Whether an inline mark would actually LAND. `applyInline` opens with
    // `if (!activeEditable) break;` in the iframe, so without this the bold
    // button would look live and do nothing.
    editingText: text.canFormat(),
    hasAlt: vm.hasAlt
  };

  let rendered = 0;
  for (const control of caps.controls) {
    const render = CONTROL_RENDERERS[control];
    // A control the registry declares but this file cannot draw yet is SKIPPED,
    // not faked. Rendering a placeholder would be a button that does nothing.
    if (!render) continue;
    const reason = caps.disabledReason(control, ctx);
    frag.appendChild(render({ vm, reason, text, image, logo, injectedReplaceService }));
    rendered++;
  }

  if (rendered > 0 && caps.allSettings) frag.appendChild(divider());

  if (caps.allSettings) {
    frag.appendChild(
      paneButton({
        label: 'All settings',
        glyph: 'sliders',
        // The focus-restore target rides along, so closing the panel returns
        // focus to the control that opened it rather than to the document.
        onClick: (e) => openElementSettings(vm, e)
      })
    );
  } else if (!isKnownEditableType(type)) {
    // Unknown type: say so rather than showing an empty bar. A pane with a
    // label and nothing else reads as broken; a pane that says why does not.
    const note = document.createElement('span');
    note.className = 'pb-cb-pane-note';
    note.textContent = 'No settings for this element';
    frag.appendChild(note);
  }

  return frag;
}

/**
 * How each declared control draws itself.
 *
 * Keyed by `CONTROL.*` so the registry and this table cannot drift apart on a
 * spelling. A control with no entry here is skipped by `buildElementPane` —
 * which is how `crop` and `fit` stay out of the bar without being deleted from
 * anyone's vocabulary: `image-command-service.js` records that they have no
 * parent-reachable writer, so a Crop button here would be a control that does
 * nothing.
 */
const CONTROL_RENDERERS = Object.freeze({
  [CONTROL.BOLD]: ({ reason, text }) =>
    toggleButton({
      label: 'Bold',
      glyph: 'bold',
      shortcut: 'Ctrl+B',
      reason,
      pressed: Boolean(text.textFormatState()?.bold),
      onClick: () => text.applyInline('bold')
    }),
  [CONTROL.ITALIC]: ({ reason, text }) =>
    toggleButton({
      label: 'Italic',
      glyph: 'italic',
      shortcut: 'Ctrl+I',
      reason,
      pressed: Boolean(text.textFormatState()?.italic),
      onClick: () => text.applyInline('italic')
    }),
  [CONTROL.SIZE]: ({ vm, reason, text, logo }) =>
    vm.editableType === 'logo'
      ? stepperControl({
          label: 'Logo height',
          reason,
          // The MEASURED height, so an untouched logo shows a real number rather
          // than a blank field — no corpus logo authors a size, so an
          // authored-value readback would be empty on essentially all of them.
          value: logo.currentSizePx(vm.payload),
          onChange: (px) => logo.setSize(vm.payload, px)
        })
      : stepperControl({
          label: 'Font size',
          reason,
          // The COMPUTED size, so the field shows what the reader sees rather
          // than an override that may not be set. `applyBlockProp` writes an
          // element override; it is not a format command, and conflating the two
          // is what the service's BLOCK_PROPS list exists to prevent.
          value:
            Math.round(Number.parseFloat(text.textFormatState()?.computedFontSize) || 0) || null,
          onChange: (px) =>
            text.applyBlockProp(vm.instanceId, vm.eid, 'fontSize', px ? `${px}px` : '', {
              label: 'Font size'
            })
        }),
  [CONTROL.ALIGN]: ({ vm, reason, text }) =>
    paneButton({
      label: 'Align',
      glyph: 'alignLeft',
      iconOnly: true,
      reason,
      onClick: (e) => openAlignMenu(e?.currentTarget, vm, text)
    }),
  [CONTROL.LINK]: ({ vm, reason, text }) =>
    paneButton({
      label: 'Link',
      glyph: 'links',
      iconOnly: true,
      shortcut: 'Ctrl+K',
      reason,
      onClick: (e) => {
        // The two types reach different writers, and neither is "the link
        // service": text wraps a SELECTION, an image links the whole element.
        //
        // Text has a parent-side writer, so row 2 acts. An image link is a
        // form — url, target, client-link mode — and that form already exists
        // in the glass panel. Row 2 opens it ON that field rather than growing
        // a second copy of it, which is also why `image-command-service.js`'s
        // `setLink` is not called from here: it writes a resolved href, and
        // nothing in row 2 can resolve one.
        if (vm.editableType === 'image') openElementSettings(vm, e, '#pb-gp-img-link-type');
        else text.applyInline('link');
      }
    }),
  [CONTROL.REWRITE]: ({ reason }) =>
    paneButton({
      label: 'Rewrite',
      glyph: 'skin',
      reason,
      onClick: () => eventBus.emit('agent:rewrite-selection')
    }),
  // Two types own this control now, and they reach DIFFERENT writers — an image
  // swaps one element, a logo swaps every logo slot in the section and
  // re-measures the aspect box. The operationId stays namespaced per type so the
  // in-flight guard still single-flights each one correctly.
  [CONTROL.REPLACE]: ({ vm, reason, injectedReplaceService }) => {
    const service = injectedReplaceService?.replace
      ? { replace: (target) => injectedReplaceService.replace(target) }
      : serviceFor(vm.payload || vm);
    const noun = kindLabel(vm.elementKind || vm.payload?.kind).toLowerCase();
    return paneButton({
      label: 'Replace',
      glyph: 'templates',
      reason: reason || (service?.replace ? null : `Replace is unavailable for this ${noun}`),
      onAsyncClick: () => service?.replace?.(vm.payload || vm),
      operationId: `${vm.elementKind || vm.editableType}:${vm.instanceId}:${targetRefKey(vm.targetRef)}:replace`,
      busyLabel: 'Replacing…',
      errorScope: `Replace ${noun}`
    });
  },
  [CONTROL.ALT]: ({ vm, reason, image, logo }) => {
    const isLogo = vm.editableType === 'logo';
    // Missing alt text is a real accessibility defect and the only control in
    // row 2 that reports one, so it says which state it is in. A logo reads its
    // state from the canonical section HTML rather than the click payload,
    // because a replace rewrites the alt beneath that snapshot.
    const state = isLogo ? logo.hasAlt(vm.payload) : imageAltState(vm, image);
    return paneButton({
      label: state === false ? 'Add alt text' : 'Alt text',
      glyph: 'flag',
      reason,
      // Unknown is neutral, not a false accessibility warning. Real image
      // payloads carry `alt`; null only means an older/incomplete producer.
      danger: state === false,
      // Free text needs a field, and the panel already has one. Opening it
      // focused beats prompting for a string in a dialog the panel would then
      // have to stay in sync with.
      onClick: (e) => openElementSettings(vm, e, isLogo ? '#pb-gp-logo-label' : '#pb-gp-img-alt')
    });
  }
});

function imageAltState(vm, image) {
  if (typeof vm.hasAlt === 'boolean') return vm.hasAlt;
  const state = typeof image?.hasAlt === 'function' ? image.hasAlt(vm.payload) : null;
  return typeof state === 'boolean' ? state : null;
}

/**
 * A number field with two steppers.
 *
 * Hand-built because the kit has no numeric stepper — `createLabeledInput` is a
 * text field and `design-widgets.js`'s sliders are panel-width. Kept to three
 * elements and one commit path so it cannot grow into a second widget library.
 *
 * Commits on `change` (blur / Enter), never on `input`: a keystroke-per-write
 * would put one history entry per digit typed.
 */
function stepperControl({ label, reason, value, onChange }) {
  const wrap = document.createElement('div');
  wrap.className = 'pb-cb-stepper';

  const field = document.createElement('input');
  field.type = 'text';
  field.inputMode = 'numeric';
  field.className = 'pb-cb-stepper-field';
  field.value = value == null ? '' : String(value);
  field.setAttribute('aria-label', reason ? `${label} — ${reason}` : label);
  field.disabled = Boolean(reason);

  const commit = (next) => {
    const px = Math.max(1, Math.min(400, Math.round(Number(next))));
    if (!Number.isFinite(px)) return;
    field.value = String(px);
    onChange(px);
  };

  const step = (delta) =>
    createButton({
      variant: 'bare',
      className: 'pb-cb-stepper-btn',
      icon: icon(delta > 0 ? 'plus' : 'minus', { size: 'xs' }),
      // The reason rides on the steppers too. A disabled control whose
      // accessible name does not say why is the exact gap this pane's whole
      // `disabledReason` contract exists to close — and the steppers are the
      // easiest place to forget it, because the FIELD carries the label.
      ariaLabel:
        (delta > 0 ? `Increase ${label.toLowerCase()}` : `Decrease ${label.toLowerCase()}`) +
        (reason ? ` — ${reason}` : ''),
      disabled: Boolean(reason),
      onClick: () => commit((Number(field.value) || 0) + delta)
    });

  field.addEventListener('change', () => {
    if (field.value.trim() === '') return void onChange(null);
    commit(field.value);
  });

  wrap.append(step(-1), field, step(1));
  return wrap;
}

/** Three choices, one popover. `textAlign` is an element override, not a mark. */
function openAlignMenu(triggerEl, vm, text) {
  openPopoverMenu(triggerEl, {
    owner: 'command-bar-align',
    align: 'left',
    width: 180,
    menuLabel: 'Text alignment',
    items: [
      { id: 'left', label: 'Align left', icon: icon('alignLeft'), value: 'left' },
      { id: 'center', label: 'Align centre', icon: icon('alignCenterH'), value: 'center' },
      { id: 'right', label: 'Align right', icon: icon('alignRight'), value: 'right' }
    ].map((item) => ({
      ...item,
      onSelect: () =>
        text.applyBlockProp(vm.instanceId, vm.eid, 'textAlign', item.value, { label: 'Align text' })
    }))
  });
}

/**
 * Open the element's settings panel, optionally landing on one field.
 *
 * ONE emitter for every route out of row 2 into a panel, so the payload
 * resolution and the focus-restore target are decided in a single place. The
 * handler resolves the payload from the settled active-editable store — row 2
 * may be acting several interactions after the click that made the selection.
 */
function openElementSettings(vm, event, focusField = null) {
  eventBus.emit('props:open-element-settings', {
    instanceId: vm.instanceId,
    eid: vm.eid,
    editableType: vm.editableType,
    focusField,
    triggerEl: event?.currentTarget || null
  });
}

/** A control with a pressed state. `aria-pressed`, not a class. */
function toggleButton({ label, glyph, shortcut, reason, pressed, onClick }) {
  const btn = paneButton({ label, glyph, shortcut, reason, iconOnly: true, onClick });
  btn.setAttribute('aria-pressed', String(Boolean(pressed)));
  return btn;
}

// ── the registry the zone reads ─────────────────────────────────────────────

/**
 * Scope kind → builder. A kind with no builder renders NOTHING but its label,
 * which is the honest degradation: CMD-6 adds text/image/element, and until it
 * does, an element selection must not silently show the section pane's verbs
 * pointed at the wrong target.
 */
export const PANE_BUILDERS = Object.freeze({
  page: buildPagePane,
  section: buildSectionPane,
  multi: buildMultiPane,
  // CMD-6: all three element scopes share ONE builder, because which controls
  // appear is a registry lookup rather than a branch. Three builders would be
  // three places to forget that there is no universal fallback.
  text: buildElementPane,
  image: buildElementPane,
  element: buildElementPane
});

/** The fallback for a scope no builder covers yet. Label only, no verbs. */
export function buildLabelOnlyPane(vm) {
  const frag = document.createDocumentFragment();
  frag.appendChild(paneLabel(vm, 'element'));
  const note = document.createElement('span');
  note.className = 'pb-cb-pane-note';
  note.textContent = 'Use the panel for this element';
  frag.appendChild(note);
  frag.appendChild(
    paneButton({
      label: 'All settings',
      glyph: 'sliders',
      onClick: () =>
        eventBus.emit('props:open-element-settings', {
          instanceId: vm.instanceId,
          eid: vm.eid,
          editableType: vm.editableType
        })
    })
  );
  return frag;
}

/** For tests: the store read a pane makes, in one place. */
export function activePageName(state = store.getState()) {
  const page = (state?.pages || []).find((p) => p?.id === state?.activePage);
  return page?.name || 'Page';
}
